import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/lib/AuthContext';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { getBackend } from '@/api/apiClient';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

import SettingsSectionCard from '@/components/settings/SettingsSectionCard';
import AccountSection from '@/components/settings/sections/AccountSection';
import OdooConnectionSection from '@/components/settings/sections/OdooConnectionSection';
import AppearanceSection from '@/components/settings/sections/AppearanceSection';
import NotificationsSection from '@/components/settings/sections/NotificationsSection';
import SyncDataSection from '@/components/settings/sections/SyncDataSection';
import SecuritySection from '@/components/settings/sections/SecuritySection';
import DiagnosticsSection from '@/components/settings/sections/DiagnosticsSection';
import AppInfoSection from '@/components/settings/sections/AppInfoSection';
import PaymentMethodsSection from '@/components/settings/sections/PaymentMethodsSection';
import CurrencySection from '@/components/settings/sections/CurrencySection';

const SECTIONS = [
  { key: 'account', icon: '👤', title: 'الحساب الشخصي', description: 'بياناتك الشخصية ومعلومات العمل' },
  { key: 'currency', icon: '💱', title: 'العملة والضريبة', description: 'عملة الشركة ونسبة ضريبة القيمة المضافة' },
  { key: 'payments', icon: '💳', title: 'وسائل الدفع', description: 'الوسائل اللي تظهر للمندوبين في التحصيل' },
  { key: 'odoo', icon: '🔌', title: 'الاتصال بـ Odoo', description: 'حالة الاتصال والخادم والتشخيص' },
  { key: 'appearance', icon: '🎨', title: 'المظهر والتخصيص', description: 'الثيم، حجم الخط، تنسيق الأرقام' },
  { key: 'notifications', icon: '🔔', title: 'الإشعارات', description: 'تفعيل وتخصيص التنبيهات' },
  { key: 'sync', icon: '🔄', title: 'المزامنة والبيانات', description: 'إدارة المزامنة والـ Cache' },
  { key: 'security', icon: '🔐', title: 'الأمان والخصوصية', description: 'كلمة المرور، القفل، الخصوصية' },
  { key: 'diagnostics', icon: '🛠️', title: 'التشخيص والدعم', description: 'فحص النظام وتقارير الأخطاء' },
  { key: 'info', icon: 'ℹ️', title: 'معلومات التطبيق', description: 'الإصدار، التقنية، القانونية' },
];

// Odoo server details and diagnostics don't apply to accounts on SellGo's own database;
// currency and payment methods are set up here only there (on Odoo they live in Odoo).
const ODOO_ONLY_SECTIONS = ['odoo', 'diagnostics'];
const SELLGO_ONLY_SECTIONS = ['currency', 'payments'];

export default function Settings() {
  const { user } = useAuth();
  const { odooSession, logoutOdoo } = useOdooAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);
  const [showLogout, setShowLogout] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const pressTimerRef = useRef(null);
  const [pressProgress, setPressProgress] = useState(0);
  const pressIntervalRef = useRef(null);

  const handlePressStart = () => {
    setPressProgress(0);
    let elapsed = 0;
    pressIntervalRef.current = setInterval(() => {
      elapsed += 50;
      setPressProgress(Math.min(100, Math.round((elapsed / 3000) * 100)));
    }, 50);
    pressTimerRef.current = setTimeout(() => {
      clearInterval(pressIntervalRef.current);
      setPressProgress(0);
      navigate('/settings/admin/odoo-config');
    }, 3000);
  };

  const handlePressEnd = () => {
    clearTimeout(pressTimerRef.current);
    clearInterval(pressIntervalRef.current);
    setPressProgress(0);
  };

  const name = odooSession?.name || user?.full_name || 'المستخدم';
  const email = odooSession?.email || user?.email || '';
  const roleLabel = user?.role === 'manager' ? 'مدير' : 'مندوب';
  const onSupabase = getBackend() === 'supabase';
  const sections = SECTIONS.filter((s) => !(onSupabase ? ODOO_ONLY_SECTIONS : SELLGO_ONLY_SECTIONS).includes(s.key));

  const handleReset = () => {
    localStorage.clear();
    setShowReset(false);
    window.location.reload();
  };

  // Sub-page rendering
  const sectionMap = {
    account: <AccountSection onBack={() => setActiveSection(null)} />,
    currency: <CurrencySection onBack={() => setActiveSection(null)} />,
    payments: <PaymentMethodsSection onBack={() => setActiveSection(null)} />,
    odoo: <OdooConnectionSection onBack={() => setActiveSection(null)} />,
    appearance: <AppearanceSection onBack={() => setActiveSection(null)} />,
    notifications: <NotificationsSection onBack={() => setActiveSection(null)} />,
    sync: <SyncDataSection onBack={() => setActiveSection(null)} />,
    security: <SecuritySection onBack={() => setActiveSection(null)} />,
    diagnostics: <DiagnosticsSection onBack={() => setActiveSection(null)} />,
    info: <AppInfoSection onBack={() => setActiveSection(null)} />,
  };

  return (
    <div className="flex flex-col min-h-full bg-background" dir="rtl">
      <AnimatePresence mode="wait">
        {activeSection ? (
          <motion.div key={activeSection} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="flex flex-col min-h-full">
            {sectionMap[activeSection]}
          </motion.div>
        ) : (
          <motion.div key="main" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col min-h-full">

            {/* Header */}
            <div className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3">
              <h1 className="text-lg font-bold text-foreground text-center">الإعدادات</h1>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-8">

              {/* User Profile Card */}
              <div className="bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-5 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-black text-2xl">{name.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-lg leading-tight truncate">{name}</p>
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full font-medium inline-block mt-1">{roleLabel}</span>
                    <p className="text-sm text-white/70 mt-1 truncate">{email}</p>
                  </div>
                </div>
                <button onClick={() => setActiveSection('account')}
                  className="mt-4 w-full bg-white/20 hover:bg-white/30 text-white text-sm font-bold py-2 rounded-xl transition-colors">
                  ✏️ تعديل الملف الشخصي
                </button>
              </div>

              {/* Connection status badge */}
              <div onClick={() => !onSupabase && setActiveSection('odoo')}
                className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3 cursor-pointer hover:border-primary/30 transition-colors">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{onSupabase ? 'متصل بـ SellGo' : 'متصل بـ Odoo 17'}</p>
                  <p className="text-xs text-muted-foreground">{odooSession?.name || 'جلسة نشطة'}</p>
                </div>
                <span className="text-xs text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded-full">✅ متصل</span>
              </div>

              {/* Section cards */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground px-1 pt-1">الإعدادات</p>
                {sections.map(section => (
                  <SettingsSectionCard
                    key={section.key}
                    icon={section.icon}
                    title={section.title}
                    description={section.description}
                    onClick={() => setActiveSection(section.key)}
                  />
                ))}
              </div>

              {/* Danger Zone */}
              <div className="bg-card border border-destructive/20 rounded-2xl overflow-hidden mt-4">
                <div className="px-4 py-3 bg-red-50 border-b border-destructive/20">
                  <p className="text-sm font-bold text-destructive">⚠️ منطقة الخطر</p>
                </div>
                <div className="p-2">
                  <button onClick={() => setShowLogout(true)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-red-50 text-right transition-colors">
                    <span className="text-base w-6 text-center">🚪</span>
                    <span className="flex-1 text-sm font-medium text-foreground">تسجيل الخروج</span>
                  </button>
                  <button onClick={() => setShowReset(true)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-red-50 text-right transition-colors">
                    <span className="text-base w-6 text-center">🔄</span>
                    <span className="flex-1 text-sm font-medium text-destructive">إعادة تعيين التطبيق</span>
                    <span className="text-xs text-muted-foreground">حذف كل البيانات المحلية</span>
                  </button>
                </div>
              </div>

              <div
                className="flex flex-col items-center gap-0.5 pb-2 cursor-default select-none"
                onMouseDown={handlePressStart}
                onMouseUp={handlePressEnd}
                onMouseLeave={handlePressEnd}
                onTouchStart={handlePressStart}
                onTouchEnd={handlePressEnd}
                onTouchCancel={handlePressEnd}
              >
                {pressProgress > 0 && (
                  <div className="w-24 h-1 bg-muted rounded-full overflow-hidden mb-1">
                    <div className="h-full bg-primary rounded-full transition-all duration-75" style={{ width: `${pressProgress}%` }} />
                  </div>
                )}
                <span className="text-xs text-muted-foreground">
                  <span className="font-bold" style={{ color: '#1E3A5F' }}>Sell</span>
                  <span className="font-bold" style={{ color: '#10B981' }}>Go</span>
                  <span className="text-muted-foreground"> v2.1.0</span>
                </span>
                <span className="text-muted-foreground" style={{ fontSize: '10px' }}>
                  Powered by <span className="font-bold" style={{ color: '#D85A4A' }}>TECH SHARK</span> <span style={{ letterSpacing: '1px', opacity: 0.6 }}>FOR TECHNOLOGY</span>
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Logout Dialog */}
      <AlertDialog open={showLogout} onOpenChange={setShowLogout}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>تسجيل الخروج</AlertDialogTitle>
            <AlertDialogDescription>هل تريد تسجيل الخروج؟ سيتم مسح بيانات الجلسة الحالية.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction onClick={logoutOdoo} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              تسجيل الخروج
            </AlertDialogAction>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Dialog */}
      <AlertDialog open={showReset} onOpenChange={setShowReset}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>إعادة تعيين التطبيق</AlertDialogTitle>
            <AlertDialogDescription>
              سيتم حذف كل البيانات المحلية والإعدادات. هل أنت متأكد؟ هذا الإجراء لا يمكن التراجع عنه.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction onClick={handleReset} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              إعادة التعيين
            </AlertDialogAction>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}